import { useEffect, useState } from 'react';
import Papa from 'papaparse';
import AlertWithIcon from '../AlertWithIcon';

import LinkButton from '../LinkButton';
import {
  P2GBox,
  P2GGrid,
  P2GCheckBoxLegacy,
  P2GButton,
  P2GProgress,
  P2GText,
  SvgConfirmCircle
} from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface CsvData {
  fields: string[];
  data: string[][];
}

interface props {
  data: () => CsvData;
  headers: () => string[];
  fileName: string;
  mode: 'manual' | 'auto';
}

export function ExportToCsv(props: props) {
  const { t } = useTranslation(TranslationNamespaces.MYACCOUNT);
  const [csv, setCsv] = useState<string>();
  const [state, setState] = useState<'IDLE' | 'PROCESSING' | 'ERROR' | 'COMPLETE'>('IDLE');
  const [exc, setExc] = useState<number[]>([]);

  const download = () => {
    setState('PROCESSING');

    const rawData = props.data();
    const data: CsvData = {
      fields: rawData.fields.filter((_, hi) => !exc.includes(hi)),
      data: rawData.data.map((d) => {
        return d.filter((_, fi) => !exc.includes(fi));
      })
    };

    const output = Papa.unparse(data);
    setCsv(output);
    setTimeout(() => {
      setState('COMPLETE');
    }, 1000);
  };

  useEffect(() => {
    if (props.mode === 'auto') {
      download();
    }
  }, [props.mode]);

  return (
    <P2GBox mb={2}>
      {state === 'IDLE' && (
        <P2GBox>
          <P2GBox pb={4} textAlign="left">
            <P2GGrid container spacing={1}>
              {props.headers().map((x, i) => (
                <P2GGrid item xs={6} key={x}>
                  <P2GCheckBoxLegacy
                    checked={!exc.includes(i)}
                    id={x}
                    label={x}
                    onChange={() => setExc(exc.includes(i) ? exc.filter((x) => x != i) : exc.concat(i))}
                    labelPlacement="end"
                    variant="primary"
                    error={false}
                  />
                </P2GGrid>
              ))}
            </P2GGrid>
          </P2GBox>
          <P2GButton onClick={download}>{t('CSV_EXPORT_BUTTON', { defaultValue: 'Export CSV' })}</P2GButton>
        </P2GBox>
      )}
      {state === 'PROCESSING' && (
        <P2GBox>
          <P2GBox mb={2}>
            <AlertWithIcon icon={<P2GProgress />}>
              <P2GText>{t('CSV_EXPORT_PROCESSING_MESSAGE', { defaultValue: 'Processing...' })}</P2GText>
            </AlertWithIcon>
          </P2GBox>
          <P2GButton disabled>{t('CSV_EXPORT_LOADING_BUTTON', { defaultValue: 'Loading...' })}</P2GButton>
        </P2GBox>
      )}
      {state === 'COMPLETE' && csv && (
        <P2GBox>
          <P2GBox mb={2}>
            <AlertWithIcon icon={<SvgConfirmCircle />}>
              <P2GText>{t('CSV_EXPORT_CREATED_MESSAGE', { defaultValue: 'Created' })}</P2GText>
            </AlertWithIcon>
          </P2GBox>
          <LinkButton
            variant="button"
            download={`${props.fileName}.csv`}
            href={'data:text/csv,' + encodeURIComponent(csv)}
          >
            {t('CSV_EXPORT_DOWNLOAD_BUTTON', { defaultValue: 'Download CSV' })}
          </LinkButton>
        </P2GBox>
      )}
    </P2GBox>
  );
}
